import { useEffect, useRef, useState } from 'react';
import { Sparkles, RefreshCw, Check, AlertTriangle, ArrowRight, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { Badge } from '@/components/common/Badge';
import { useToast } from '@/components/common/Toast';
import {
  getConnectors,
  enrichAccount,
  getAccountEnrichmentStatus,
  promoteAccountEnrichment,
  type AccountEnrichment,
} from '@/features/prospecting/services/prospectingService';

interface Props {
  accountId: string;
  /** Called after a promote so the parent can refetch the account. */
  onPromoted?: () => void;
}

const POLL_MS = 4000;

export default function AccountEnrichmentPanel({ accountId, onPromoted }: Props) {
  const { addToast } = useToast();
  const [data, setData] = useState<AccountEnrichment | null>(null);
  const [llmConnectorId, setLlmConnectorId] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [promoting, setPromoting] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearPoll = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const load = async () => {
    try {
      const d = await getAccountEnrichmentStatus(accountId);
      setData(d);
      if (d.enrichmentStatus !== 'RUNNING') clearPoll();
    } catch {
      // account may simply have no enrichment yet — ignore
    }
  };

  useEffect(() => {
    load();
    // Pick a configured, active LLM connector to run with.
    getConnectors()
      .then((cons) => {
        const llm = cons.find((c) => c.category === 'LLM' && c.isConfigured && c.isActive)
          || cons.find((c) => c.category === 'LLM' && c.isConfigured);
        setLlmConnectorId(llm ? llm.id : null);
      })
      .catch(() => setLlmConnectorId(null));
    return clearPoll;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  const startPolling = () => {
    clearPoll();
    pollRef.current = setInterval(load, POLL_MS);
  };

  const run = async () => {
    if (!llmConnectorId) {
      addToast({ type: 'error', title: 'No LLM connector', description: 'Configure an LLM connector in Data Sources first.' });
      return;
    }
    setStarting(true);
    try {
      await enrichAccount(accountId, { llm: llmConnectorId });
      addToast({ type: 'success', title: 'Enrichment started', description: 'Running Vibe Prospecting on this account.' });
      setData((d) => (d ? { ...d, enrichmentStatus: 'RUNNING' } : d));
      startPolling();
    } catch (e: any) {
      addToast({ type: 'error', title: 'Could not start', description: e?.response?.data?.error || e.message });
    } finally {
      setStarting(false);
    }
  };

  const promote = async (field: string) => {
    setPromoting(field);
    try {
      await promoteAccountEnrichment(accountId, [field]);
      addToast({ type: 'success', title: 'Applied', description: 'Analyzed value copied to the account field.' });
      onPromoted?.();
    } catch (e: any) {
      addToast({ type: 'error', title: 'Could not apply', description: e?.response?.data?.error || e.message });
    } finally {
      setPromoting(null);
    }
  };

  const status = data?.enrichmentStatus ?? null;
  const running = status === 'RUNNING';
  const hasAnalysis = !!(data && (data.analyzedPainAxis || data.analyzedMaturityBand || data.analyzedWinTheme || data.analyzedIcpScore != null || data.analyzedContact || data.analyzedEstimatedSize || data.analyzedPlantType));
  const kit = data?.analyzedOutreachKit || null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            Vibe Prospecting Enrichment
          </CardTitle>
          <div className="flex items-center gap-2">
            {status && (
              <Badge variant={running ? 'secondary' : status === 'DONE' ? 'active' : 'destructive'}>
                {running ? 'Running…' : status === 'DONE' ? 'Analyzed' : 'Failed'}
              </Badge>
            )}
            <Button size="sm" onClick={run} disabled={starting || running}>
              {running ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              {hasAnalysis ? 'Re-run' : 'Enrich with AI'}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {!llmConnectorId && (
          <div className="flex items-start gap-2 text-sm text-muted-foreground rounded-md border border-dashed p-3">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>No LLM connector is configured. Add one in Data Sources to enable AI enrichment.</span>
          </div>
        )}

        {running && (
          <p className="text-sm text-muted-foreground">
            The 5-agent pipeline is analysing this company — pain axis, maturity, strategy, contact, and outreach. This usually takes under a minute.
          </p>
        )}

        {!running && !hasAnalysis && (
          <p className="text-sm text-muted-foreground">
            Run Vibe Prospecting to gather pain axis, maturity band, ICP score, win theme, architecture tags, and a ready-to-use outreach kit for this account. Results land as suggestions below — your entered values are never overwritten.
          </p>
        )}

        {hasAnalysis && (
          <>
            {data?.enrichedAt && (
              <p className="text-xs text-muted-foreground">
                Analyzed {new Date(data.enrichedAt).toLocaleString()}
              </p>
            )}

            {/* Side-by-side: analyzed suggestion vs. your entered value */}
            <div className="space-y-3">
              <CompareRow
                label="Pain axis"
                analyzed={data?.analyzedPainAxis}
                human={data?.primaryPainAxis}
                canPromote={!!data?.analyzedPainAxis && data?.analyzedPainAxis !== data?.primaryPainAxis}
                promoting={promoting === 'painAxis'}
                onPromote={() => promote('painAxis')}
              />
              <CompareRow
                label="Maturity band"
                analyzed={data?.analyzedMaturityBand}
                human={data?.maturityBand}
                canPromote={!!data?.analyzedMaturityBand && data?.analyzedMaturityBand !== data?.maturityBand}
                promoting={promoting === 'maturityBand'}
                onPromote={() => promote('maturityBand')}
              />
              <CompareRow
                label="ICP score → tier"
                analyzed={data?.analyzedIcpScore != null ? String(data?.analyzedIcpScore) : null}
                human={null}
                canPromote={data?.analyzedIcpScore != null}
                promoting={promoting === 'icpScore'}
                onPromote={() => promote('icpScore')}
                promoteLabel="Set tier"
              />
            </div>

            {data?.analyzedPainRationale && (
              <Field label="Pain rationale">{data.analyzedPainRationale}</Field>
            )}

            {/* Company profile */}
            {(data?.analyzedEstimatedSize || data?.analyzedDataReadiness || data?.analyzedAutomationStage || data?.analyzedCompetitiveAngle || data?.analyzedLinkedInUrl || data?.analyzedPlantType) && (
              <div className="grid gap-3 sm:grid-cols-2 rounded-md border p-3">
                {data?.analyzedPlantType && (
                  <div>
                    <div className="flex items-center justify-between">
                      <FieldLabel>Plant type</FieldLabel>
                      {data.analyzedPlantType !== data.plantType && (
                        <Button size="sm" variant="ghost" disabled={promoting === 'plantType'} onClick={() => promote('plantType')}>
                          <Check className="h-3.5 w-3.5 mr-1" /> Use this
                        </Button>
                      )}
                    </div>
                    <div className="mt-1 text-sm">{data.analyzedPlantType}{data.plantType && data.plantType !== data.analyzedPlantType ? <span className="text-muted-foreground"> · entered: {data.plantType}</span> : null}</div>
                  </div>
                )}
                {data?.analyzedEstimatedSize && <Field label="Company size">{data.analyzedEstimatedSize}</Field>}
                {data?.analyzedDataReadiness && <Field label="Data readiness">{data.analyzedDataReadiness}</Field>}
                {data?.analyzedAutomationStage && <Field label="Automation stage">{data.analyzedAutomationStage}</Field>}
                {data?.analyzedCompetitiveAngle && <Field label="Competitive angle">{data.analyzedCompetitiveAngle}</Field>}
                {data?.analyzedLinkedInUrl && (
                  <Field label="Company LinkedIn">
                    <a href={data.analyzedLinkedInUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
                      <ExternalLink className="h-3 w-3" /> View
                    </a>
                  </Field>
                )}
              </div>
            )}

            {/* Discovered contact */}
            {data?.analyzedContact && (data.analyzedContact.name || data.analyzedContact.email || data.analyzedContact.title) && (
              <div className="grid gap-3 sm:grid-cols-2 rounded-md border p-3">
                <div className="sm:col-span-2"><FieldLabel>Discovered contact</FieldLabel></div>
                {data.analyzedContact.name && <Field label="Name">{data.analyzedContact.name}</Field>}
                {data.analyzedContact.title && <Field label="Title">{data.analyzedContact.title}</Field>}
                {data.analyzedContact.dept && <Field label="Department">{data.analyzedContact.dept}</Field>}
                {data.analyzedContact.email && (
                  <Field label="Email"><a href={`mailto:${data.analyzedContact.email}`} className="text-primary hover:underline">{data.analyzedContact.email}</a></Field>
                )}
                {data.analyzedContact.phone && <Field label="Phone">{data.analyzedContact.phone}</Field>}
                {data.analyzedContact.linkedIn && (
                  <Field label="LinkedIn">
                    <a href={data.analyzedContact.linkedIn} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
                      <ExternalLink className="h-3 w-3" /> View
                    </a>
                  </Field>
                )}
              </div>
            )}

            {data?.analyzedWinTheme && (
              <Field label="Win theme">
                <blockquote className="border-l-2 pl-3 italic">{data.analyzedWinTheme}</blockquote>
              </Field>
            )}

            {!!data?.analyzedArchitectureTags?.length && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <FieldLabel>Architecture tags</FieldLabel>
                  <Button size="sm" variant="ghost" disabled={promoting === 'architectureTags'} onClick={() => promote('architectureTags')}>
                    <ArrowRight className="h-3.5 w-3.5 mr-1" /> Merge into account
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {data.analyzedArchitectureTags.map((t) => <Badge key={t} variant="outline">{t}</Badge>)}
                </div>
              </div>
            )}

            {!!data?.analyzedCantieroCases?.length && (
              <div className="space-y-2">
                <FieldLabel>Cantier use cases</FieldLabel>
                <div className="flex flex-wrap gap-1.5">
                  {data.analyzedCantieroCases.map((c) => <Badge key={c} variant="secondary">{c}</Badge>)}
                </div>
              </div>
            )}

            {!!data?.analyzedSignals?.length && (
              <div className="space-y-2">
                <FieldLabel>Buying signals</FieldLabel>
                <ul className="list-disc pl-5 text-sm space-y-1">
                  {data.analyzedSignals.slice(0, 6).map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}

            {kit && (kit.emailSubject || kit.emailBody || kit.callScript || kit.linkedInDm) && (
              <div className="space-y-3">
                <FieldLabel>Outreach kit</FieldLabel>
                {kit.emailSubject && (
                  <Field label="Email subject">{kit.emailSubject}</Field>
                )}
                {kit.emailBody && (
                  <Field label="Email body"><pre className="whitespace-pre-wrap font-sans text-sm">{kit.emailBody}</pre></Field>
                )}
                {kit.callScript && (
                  <Field label="Call script"><pre className="whitespace-pre-wrap font-sans text-sm">{kit.callScript}</pre></Field>
                )}
                {kit.linkedInDm && (
                  <Field label="LinkedIn DM"><pre className="whitespace-pre-wrap font-sans text-sm">{kit.linkedInDm}</pre></Field>
                )}
              </div>
            )}

            {!!data?.analyzedSourceUrls?.length && (
              <div className="space-y-2">
                <FieldLabel>Sources</FieldLabel>
                <ul className="space-y-1">
                  {data.analyzedSourceUrls.map((u, i) => (
                    <li key={i}>
                      <a href={u} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
                        <ExternalLink className="h-3 w-3" /> {u}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{children}</label>;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <div className="mt-1 text-sm">{children}</div>
    </div>
  );
}

function CompareRow({
  label, analyzed, human, canPromote, promoting, onPromote, promoteLabel = 'Use this',
}: {
  label: string;
  analyzed?: string | null;
  human?: string | null;
  canPromote: boolean;
  promoting: boolean;
  onPromote: () => void;
  promoteLabel?: string;
}) {
  return (
    <div className="grid grid-cols-[1fr_auto] items-center gap-3 rounded-md border p-3">
      <div className="grid gap-1 sm:grid-cols-2">
        <div>
          <FieldLabel>Analyzed</FieldLabel>
          <div className="mt-0.5 text-sm font-medium">{analyzed || <span className="text-muted-foreground">—</span>}</div>
        </div>
        <div>
          <FieldLabel>Entered</FieldLabel>
          <div className="mt-0.5 text-sm">{human || <span className="text-muted-foreground">not set</span>}</div>
        </div>
      </div>
      <div className="justify-self-end">
        {canPromote ? (
          <Button size="sm" variant="outline" disabled={promoting} onClick={onPromote}>
            {promoting ? <RefreshCw className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-1" />}
            {promoteLabel}
          </Button>
        ) : (
          <span className="text-xs text-muted-foreground">{analyzed && analyzed === human ? 'Matches' : ''}</span>
        )}
      </div>
    </div>
  );
}
